/** Console v0.5: Record a meeting (CAP-009.H11a). Hosts today's recorder -- the start screen (session check first,
 * sound check, headphones, the lawyer-approved consent tick) and the live screen (Stop with its confirmation,
 * Discard, uploads, the 55-minute cap) -- unchanged, inside the new frame and with the phone layout of the Recorder
 * mock-up, plus Pause / Continue (H11b: A's s.21 backend, D's six conditions; today's console doesn't offer it). Like today's console, the token and key stay in memory only: nothing here writes browser storage or
 * the address bar (the recorder guard test reads this file too). */

import { fetchMe } from "../api.js";
import { discardUnsent, loadUnsent, renderUnsentBanners, uploadUnsent } from "../recorder-banner.js";
import { confirmDialog, startLiveRecording } from "../recorder-live.js";
import type { LiveRecording } from "../recorder-live.js";
import { mountRecorderStart } from "../recorder-start.js";
import type { Mounted } from "../recorder-start.js";
import { DISCARD_FAILED, SIGNED_OUT, isSignedOut, signOutCopy } from "../recorder.js";
import type { RecoverableRecording } from "../recorder/types.js";
import type { ScreenContext } from "./app.js";
import { HOOKS, SCREENS, updateBanner } from "./app.js";
import { card, h, legalFooter, linkButton, pageHead } from "./ui.js";

/** E's finding 5: with no client to choose, Start could never be pressed and nothing said why. */
export const NO_CLIENTS_COPY = {
  text: "There's no client you can record a meeting for yet. A meeting is recorded for a client whose Xero organisation is connected to Preekie and that you have access to.",
  next: "An account admin can connect one on Connections, or give you access on Team.",
  action: "Open Connections",
} as const;

// The live recording outlives the screen: moving to another page keeps it recording (as today's console does), and
// coming back re-attaches it. One per tab.
let live: LiveRecording | null = null;
let liveRecordingId: string | null = null;
let liveNode: HTMLElement | null = null;
const unsent = { recordings: [] as RecoverableRecording[], uploading: new Set<string>(), notice: null as string | null };
let onRecordPage = false;
let goRecord: (() => void) | null = null;
let tz = "Australia/Sydney";

async function refreshUnsent(): Promise<void> {
  const busy = live && liveRecordingId && live.busyWith(liveRecordingId) ? liveRecordingId : null;
  unsent.recordings = await loadUnsent(busy);
  updateBanner();
}

/** The banner every page shows: "You're recording a meeting." elsewhere, and audio still waiting on this computer. */
function banner(): HTMLElement | null {
  const liveElsewhere = !!live && live.recording() && !onRecordPage;
  if (!liveElsewhere && unsent.recordings.length === 0) return null;
  const node = h("div", "pk5-recorder-banner");
  renderUnsentBanners(node, {
    recordings: unsent.recordings,
    uploading: unsent.uploading,
    timeZone: tz,
    now: Date.now(),
    liveRecording: liveElsewhere,
    notice: unsent.notice,
    onBackToRecording: () => goRecord?.(),
    onUpload: (id) => {
      if (live && live.busyWith(id)) return; // never a second resume for the live engine's recording
      unsent.uploading.add(id);
      unsent.notice = null;
      updateBanner();
      void uploadUnsent(id).then((n) => {
        unsent.notice = n;
        unsent.uploading.delete(id);
        void refreshUnsent();
      });
    },
    onDiscard: (id, keepUploaded) => {
      unsent.notice = null;
      void discardUnsent(id, keepUploaded)
        .catch((err: unknown) => {
          if (isSignedOut(err)) unsent.notice = SIGNED_OUT.message;
          else if ((err as { status?: number } | null)?.status !== 409) unsent.notice = DISCARD_FAILED;
        })
        .finally(() => void refreshUnsent());
    },
  });
  return node;
}

/** Signing out with a recording running or audio waiting: today's words and confirmation first. */
function beforeSignOut(proceed: () => void): void {
  const minutes = (live?.unsentMinutes() ?? 0) + unsent.recordings.reduce((n, r) => n + r.unsent.minutes, 0);
  const copy = signOutCopy(live?.recording() ?? false, minutes);
  if (!copy) return proceed();
  confirmDialog({
    question: copy.question,
    confirm: copy.confirm,
    cancel: copy.cancel,
    onConfirm: () => {
      const stopping = live?.recording() ? live.stopForSignOut() : Promise.resolve();
      void stopping.catch(() => undefined).then(() => {
        live?.shutdown();
        live = null;
        liveRecordingId = null;
        proceed();
      });
    },
  });
}

/** Called once when the new console starts (where Meetings is on): the banner and the sign-out check. */
export function initRecorderHost(timeZone: string, navigate: (path: string) => void): void {
  tz = timeZone;
  goRecord = () => navigate("/meetings/record");
  HOOKS.banner = banner;
  HOOKS.beforeSignOut = beforeSignOut;
  void refreshUnsent();
}

function record(ctx: ScreenContext): () => void {
  onRecordPage = true;
  updateBanner();
  const client = ctx.route.kind === "record" ? ctx.route.client : null;
  const stage = h("div", "pk5-recorder");
  ctx.root.replaceChildren(pageHead("Record a meeting", null), stage, legalFooter());
  let start: Mounted | null = null;

  // One switch for both screens: the start shows the pause wording (2026-10-24) only where the live screen offers Pause.
  const pauseOn = ctx.me.recording_limits?.pause_enabled === true;
  // CAP-010: Meetings / Record a meeting / <client> -- the client once one is chosen.
  const crumbsFor = (slug: string | null) => {
    const name = (ctx.me.entities ?? []).find((c) => c.client_slug === slug)?.client_name;
    ctx.setCrumbs([{ label: "Meetings", href: "/meetings" }, { label: "Record a meeting" }, ...(name ? [{ label: name }] : [])]);
  };
  const showStart = (clientSlug: string | null) => {
    start?.destroy();
    start = mountRecorderStart({
      root: stage,
      clients: ctx.me.entities ?? [],
      clientSlug,
      recordingAvailable: true,
      timeZone: ctx.timeZone,
      recordingLimitMinutes: ctx.me.recording_limits?.limit_minutes ?? null,
      pauseOffered: pauseOn,
      clientCards: true,
      onClientChange: crumbsFor,
      // #303: the session first, before the sound check and consent.
      checkSession: fetchMe,
      // Sign in comes back here with the same client: the sign-in page reads the address it was opened at.
      onSignIn: (slug) => location.assign(slug ? `/meetings/record?client=${encodeURIComponent(slug)}` : "/meetings/record"),
      onStarted: (started, streams, startState) => {
        live?.shutdown();
        liveRecordingId = started.recording_id;
        liveNode = stage;
        live = startLiveRecording({
          root: stage,
          streams,
          started,
          state: startState,
          clientName: (ctx.me.entities ?? []).find((c) => c.client_slug === startState.clientSlug)?.client_name ?? startState.clientSlug,
          timeZone: ctx.timeZone,
          onOpenMeeting: (meetingId) => ctx.navigate(`/meetings/${meetingId}`),
          onRecordAgain: () => {
            live?.shutdown();
            live = null;
            liveRecordingId = null;
            showStart(startState.clientSlug || null);
          },
          onSignIn: () => location.assign("/meetings/record"),
          onSettled: () => void refreshUnsent(),
          // CAP-009.H11b: Pause / Continue in the new console only, and only where the server's switch is on (missing = off).
          allowPause: pauseOn,
        });
      },
    });
  };

  if (live && (live.recording() || (liveRecordingId !== null && live.busyWith(liveRecordingId)))) {
    // A recording under way (or still uploading) owns this page until it's done.
    live.attach(stage);
    liveNode = stage;
  } else if (!(ctx.me.entities ?? []).length) {
    // Nothing to record for (the server only accepts a known client): say why, instead of a Start that never enables.
    stage.append(card(undefined, h("p", null, NO_CLIENTS_COPY.text), h("p", "pk5-muted", NO_CLIENTS_COPY.next), linkButton(NO_CLIENTS_COPY.action, "/connections", "secondary")));
    crumbsFor(null); // E's finding 9: Meetings / Record a meeting here too
  } else {
    showStart(client);
    const clients = ctx.me.entities ?? [];
    crumbsFor(client ?? (clients.length === 1 ? clients[0]!.client_slug : null));
  }

  return () => {
    onRecordPage = false;
    start?.destroy();
    if (liveNode === stage) liveNode = null;
    updateBanner();
  };
}

SCREENS.record = record;
